import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  BsQuestionCircleFill,
  BsSendFill,
  BsEnvelopeFill,
  BsChatLeftQuoteFill,
  BsCheckCircleFill,
  BsExclamationTriangleFill,
  BsLightningChargeFill,
  BsShieldLockFill,
} from "react-icons/bs";

const AskQuestion = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Inquiry Portal | NGO Waters";
  }, []);

  const [formData, setFormData] = useState({ email: "", question: "" });
  const [submissionStatus, setSubmissionStatus] = useState({
    type: null,
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // States to manage custom floating label border animations
  const [focusField, setFocusField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionStatus({ type: null, message: "" });

    try {
      const targetBaseUrl =
        import.meta.env?.VITE_API_URL || "http://localhost:5000";
      const response = await axios.post(
        `${targetBaseUrl}/api/questions`,
        formData
      );

      setSubmissionStatus({
        type: "success",
        message:
          response.data?.message ||
          "Inquiry uploaded to secure server. We'll reach out shortly.",
      });
      setFormData({ email: "", question: "" });
    } catch (error) {
      setSubmissionStatus({
        type: "error",
        message:
          "Failed to dispatch. Please verify link or connection metrics.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      className="container-fluid py-5 d-flex align-items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{
        fontFamily: "'Inter', sans-serif",
        background:
          "radial-gradient(circle at top right, #f4f8fa 0%, #eef2f5 50%, #e4ecf0 100%)",
        minHeight: "100vh",
      }}
    >
      <div className="container" style={{ maxWidth: "1050px" }}>
        <div className="row g-5 align-items-center">
          {/* Left Column: Premium Brand Value Context */}
          <motion.div
            className="col-lg-5 text-center text-lg-start"
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 60, damping: 15 }}
          >
            <span
              className="badge px-3 py-2 mb-3 fw-semibold text-primary"
              style={{
                backgroundColor: "rgba(26, 60, 107, 0.06)",
                borderRadius: "20px",
                fontSize: "0.78rem",
              }}
            >
              <BsLightningChargeFill className="me-1" /> 24/7 Support Radial
            </span>
            <h1
              className="fw-extrabold text-dark tracking-tight mb-3"
              style={{
                fontSize: "2.6rem",
                letterSpacing: "-1.5px",
                lineHeight: "1.1",
              }}
            >
              Got Questions? <br />
              <span className="text-primary">We Have Answers.</span>
            </h1>
            <p
              className="text-secondary mb-4 fs-6"
              style={{ lineHeight: "1.6" }}
            >
              Submit your system parameters, operational requirements, or
              enterprise inquiries directly into our real-time processing
              stream.
            </p>

            {/* Micro Feature Indicators */}
            <div className="d-flex flex-column gap-3 max-w-sm mx-auto mx-lg-0 pt-2">
              <div className="d-flex align-items-center gap-3 justify-content-center justify-content-lg-start">
                <div className="bg-white p-2.5 rounded-3 shadow-xs text-primary">
                  <BsShieldLockFill size={18} />
                </div>
                <div className="text-start">
                  <h6 className="m-0 fw-bold text-dark small">
                    Encrypted Channels
                  </h6>
                  <p
                    className="m-0 text-muted ultra-small"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Your data layers are securely logged internally.
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 justify-content-center justify-content-lg-start">
                <div className="bg-white p-2.5 rounded-3 shadow-xs text-success">
                  <BsCheckCircleFill size={18} />
                </div>
                <div className="text-start">
                  <h6 className="m-0 fw-bold text-dark small">
                    SLA Response Guarantee
                  </h6>
                  <p
                    className="m-0 text-muted ultra-small"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Verified operators reply within 12 business hours.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Next-Gen SaaS Glassmorphic Container Form */}
          <div className="col-lg-7">
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 60, damping: 15 }}
              className="card border-0 p-4 p-md-5 bg-white shadow-lg"
              style={{
                borderRadius: "24px",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-4 justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <BsQuestionCircleFill className="text-primary" size={20} />
                  <h4
                    className="m-0 fw-bold text-dark"
                    style={{ letterSpacing: "-0.5px" }}
                  >
                    New Ticket Ticket
                  </h4>
                </div>
                <span
                  className="text-muted small"
                  style={{ fontSize: "0.8rem" }}
                >
                  * Required Fields
                </span>
              </div>

              {/* Status Banner Layout */}
              <AnimatePresence mode="wait">
                {submissionStatus.message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="alert d-flex align-items-center gap-2.5 p-3 mb-4 small border-0"
                    style={{
                      backgroundColor:
                        submissionStatus.type === "success"
                          ? "#e6f4ea"
                          : "#fce8e6",
                      color:
                        submissionStatus.type === "success"
                          ? "#137333"
                          : "#c5221f",
                      borderRadius: "12px",
                    }}
                  >
                    {submissionStatus.type === "success" ? (
                      <BsCheckCircleFill className="flex-shrink-0" />
                    ) : (
                      <BsExclamationTriangleFill className="flex-shrink-0" />
                    )}
                    <span className="fw-medium">
                      {submissionStatus.message}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Custom Animated Form */}
              <form onSubmit={handleSubmit}>
                {/* Custom Styled Email Input Container */}
                <div className="mb-4">
                  <label className="form-label small fw-bold text-dark mb-1.5 d-flex align-items-center gap-2">
                    <BsEnvelopeFill className="text-muted" size={13} /> Email
                    Address *
                  </label>
                  <div className="position-relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control px-3.5 py-3 border"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="e.g., developer.hariom@example.com"
                      onFocus={() => setFocusField("email")}
                      onBlur={() => setFocusField(null)}
                      style={{
                        borderRadius: "12px",
                        fontSize: "0.9rem",
                        backgroundColor:
                          focusField === "email" ? "#fff" : "#f8fafc",
                        borderColor:
                          focusField === "email" ? "#1a3c6b" : "#e2e8f0",
                        boxShadow:
                          focusField === "email"
                            ? "0 0 0 4px rgba(26, 60, 107, 0.08)"
                            : "none",
                        transition: "all 0.2s ease-in-out",
                        outline: "none",
                      }}
                      required
                    />
                  </div>
                </div>

                {/* Custom Styled Question Textarea Container */}
                <div className="mb-4">
                  <label className="form-label small fw-bold text-dark mb-1.5 d-flex align-items-center gap-2">
                    <BsChatLeftQuoteFill className="text-muted" size={13} />{" "}
                    Your Core Inquiry *
                  </label>
                  <textarea
                    id="question"
                    name="question"
                    className="form-control px-3.5 py-3 border"
                    rows="5"
                    value={formData.question}
                    onChange={handleChange}
                    placeholder="Describe your architecture requirements, compliance queries, or order specifics here..."
                    onFocus={() => setFocusField("question")}
                    onBlur={() => setFocusField(null)}
                    style={{
                      borderRadius: "12px",
                      fontSize: "0.9rem",
                      resize: "none",
                      backgroundColor:
                        focusField === "question" ? "#fff" : "#f8fafc",
                      borderColor:
                        focusField === "question" ? "#1a3c6b" : "#e2e8f0",
                      boxShadow:
                        focusField === "question"
                          ? "0 0 0 4px rgba(26, 60, 107, 0.08)"
                          : "none",
                      transition: "all 0.2s ease-in-out",
                      outline: "none",
                    }}
                    required
                  />
                </div>

                {/* Submit Action Block */}
                <div className="d-grid">
                  <motion.button
                    whileHover={{
                      y: -1,
                      boxShadow: "0 8px 20px rgba(26, 60, 107, 0.25)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting}
                    className="btn text-white py-3 fw-bold border-0 d-flex align-items-center justify-content-center gap-2 shadow"
                    style={{
                      backgroundColor: "#1a3c6b",
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span>Transmitting Query Layer...</span>
                      </>
                    ) : (
                      <>
                        <BsSendFill size={13} /> Dispatch Inquiry File
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer Branding Layer */}
      <div
        className="position-absolute bottom-0 start-50 translate-middle-x mb-4 text-center text-muted"
        style={{ fontSize: "0.72rem", letterSpacing: "0.5px", opacity: 0.7 }}
      >
        PONIA FOOD AND BEVERAGE PRIVATE LIMITED • INTERNAL PIPELINE SECURED BY
        HANDLE web_developer_hariom
      </div>
    </motion.div>
  );
};

export default AskQuestion;
